import React, { useReducer, useContext, useMemo, useEffect } from 'react';
import ErrorBoundary from '../../../../../../Components/ErrorBoundary/ErrorBoundary';
import EDView from '../../../../../../BaseComponents/View/EDView';
import VyValjare from '../VyValjare/VyValjare';
import { IClientBusinessModel } from '../../../../../../Types/ClientModelTypes';
import { adjust, mergeLeft, evolve, includes, times, identity } from 'ramda';
import { BusinessDataFormContext } from '../BusinessDataForm/BusinessDataForm';
import BusinessDataFormEditMenu from '../BusinessDataForm/BusinessDataFormEditMenu/BusinessDataFormEditMenu';
import { InternalError } from '../../../../../../Components/InternalError/InternalError';
import EDMasonry from '../../../../../../BaseComponents/Masonry/EDMasonry';
import EDGrid from '../../../../../../BaseComponents/GridComponents/EDGrid';
import {
  IFunctionMenuItem,
  SideBySideWidthType,
} from '../../../../../../Utils/ObjektVyUtils';
import {
  useIsLargeScreen,
  useIsExtraSmallScreen,
} from '../../../../../../Hooks/useFullscreenView';
import FunctionMenu from '../../FunctionMenu/FunctionMenu/FunctionMenu';
import FunctionItem from '../../FunctionMenu/FunctionItem/FunctionItem';
import { createPortal } from 'react-dom';
import { VisningsTypContext } from '../VisningsTypContext/VisningsTypContext';
import { RutinSettingsContext } from '../../RutinAvailabilityContext/RutinAvailabilityContext';
import { rutiner } from '../../../Const/Rutiner';
import StepperBar from '../StepperBar/StepperBar';
import { BreadcrumbsContext } from '../../RutinBreadcrumbs/BreadcrumbsContext';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import { useIsFloatingMenuVisible } from '../../../../../../Components/FloatingBottomArea/FloatingMenu';

interface IProps {
  adjustmentButtonsLabel?: string;
  accordionMode?: 'always' | 'onMobile' | 'never';
  /** You can get the value set by the adjustment buttons by using localstorage "aveny:{Collection name of model used}:sideBySideWidth" */
  canAdjustSidaVidSida?: boolean;
  children: React.ReactNode;
  nodeLoading: boolean;
  funktionsMenyItems?: IFunctionMenuItem[];
  sidaVidSida?: boolean;
  sideBySideWidths?: SideBySideWidthType[];
  /** Only works with canAdjustSidaVidSida */
  minSideBySideWidths?: SideBySideWidthType[];
  onCancel?: () => void;
  editImplemented?: boolean;
  associatedCollection?: any;
  hideEditMenu?: boolean;
  editButtonDisabled?: boolean;
  editButtonDisabledReason?: string;
  error?: Error;
  initialActivePages?: number[];
}

export interface ISidTyp {
  caption: string;
  collectionName: string;
  aktiv: boolean;
  size?: SideBySideWidthType;
  minSize?: SideBySideWidthType;
  hidden: boolean;
}

const pageCount = (children: React.ReactNode): number =>
  Array.isArray(children) ? children.filter(identity).length : 1;

export interface IObjektVyContextValue {
  adjustmentButtonsLabel?: string;
  businessModel?: IClientBusinessModel;
  canAdjustSidaVidSida: boolean;
  sidaVidSida: boolean;
  sidor: ISidTyp[];
  activePages: ISidTyp[];
  accordionMode: boolean;
}

export type ObjektVyContextValueAndDispatch = [
  IObjektVyContextValue,
  React.Dispatch<ObjektVyAction>
];

export const ObjektVyContext =
  React.createContext<ObjektVyContextValueAndDispatch>([
    {
      canAdjustSidaVidSida: false,
      sidaVidSida: false,
      sidor: [],
      activePages: [],
      accordionMode: false,
    },
    () => {},
  ]);

type ObjektVyAction =
  | {
      type: 'setCaption';
      sidNr: number;
      caption: string;
      collectionName: string;
      hidden: boolean;
    }
  | { type: 'setAktuellaVyer'; aktuellaVyer: number[] }
  | { type: 'setSidaVidSida'; sidaVidSida: boolean }
  | {
      type: 'setSidaVidSidaWidth';
      increaseSizeBy?: SideBySideWidthType;
      side?: 'left' | 'right';
      /** Use this to set new widths for the sides instead of doing it by stepping */
      widths?: SideBySideWidthType[];
    };

export const vyReducer = (
  state: IObjektVyContextValue,
  action: ObjektVyAction
): IObjektVyContextValue => {
  switch (action.type) {
    case 'setCaption':
      if (
        state.sidor[action.sidNr]?.caption === action.caption &&
        state.sidor[action.sidNr]?.hidden === action.hidden
      ) {
        return state;
      }
      return evolve(
        {
          sidor: adjust<ISidTyp>(
            action.sidNr - 1,
            mergeLeft({
              caption: action.caption,
              collectionName: action.collectionName,
              hidden: action.hidden,
            })
          ),
        },
        state
      );
    case 'setAktuellaVyer': {
      const activePages = state.sidor.filter((sida, idx) =>
        includes(idx, action.aktuellaVyer)
      );
      return {
        ...state,
        sidaVidSida: action.aktuellaVyer.length > 1,
        sidor: state.sidor.map((sida, idx) => ({
          ...sida,
          aktiv: includes(idx, action.aktuellaVyer),
        })),
        activePages: activePages,
      };
    }
    case 'setSidaVidSida': {
      const newPages = state.sidor.map((page, index) => ({
        ...page,
        aktiv: action.sidaVidSida ? index <= 1 : index === 0,
      }));
      return {
        ...state,
        sidaVidSida: action.sidaVidSida,
        sidor: newPages,
        activePages: action.sidaVidSida
          ? state.sidor.slice(0, 2)
          : [state.sidor[0]],
      };
    }
    case 'setSidaVidSidaWidth': {
      const oldLeft = state.sidor?.[0]?.size ?? 6;
      const oldRight = state.sidor?.[1]?.size ?? 6;

      const increaseSizeBy = action?.increaseSizeBy ?? 1;
      const leftMinSize = state.sidor?.[0]?.minSize ?? 1;
      const rightMinSize = state.sidor?.[1]?.minSize ?? 1;

      const newWidthsFromAction = action.widths;

      let newLeft: number = oldLeft;
      let newRight: number = oldRight;

      if (action?.side === 'right') {
        newLeft = oldLeft + increaseSizeBy;
        newRight = oldRight - increaseSizeBy;

        if (newLeft > 11) {
          const newLeftDecrease = newLeft - 11;

          newLeft -= newLeftDecrease;
          newRight += newLeftDecrease;
        }

        if (newRight < rightMinSize) {
          const newRightIncrease = rightMinSize - newRight;
          newRight += newRightIncrease;
          newLeft -= newRightIncrease;
        }
      }

      if (action?.side === 'left') {
        newLeft = oldLeft - increaseSizeBy;
        newRight = oldRight + increaseSizeBy;

        if (newRight > 11) {
          const newRightDecrease = newRight - 11;

          newRight -= newRightDecrease;
          newLeft += newRightDecrease;
        }

        if (newLeft < leftMinSize) {
          const newLeftIncrease = leftMinSize - newLeft;
          newLeft += newLeftIncrease;
          newRight -= newLeftIncrease;
        }
      }

      const newWidths = newWidthsFromAction
        ? newWidthsFromAction
        : [newLeft as SideBySideWidthType, newRight as SideBySideWidthType];

      const newPages = state.sidor.map((page, index) => ({
        ...page,
        size: newWidths?.[index],
      }));

      localStorage.setItem(
        `aveny:${state.businessModel?.CollectionName}:sideBySideWidth`,
        JSON.stringify(newWidths)
      );
      return {
        ...state,
        sidor: newPages,
      };
    }
  }
};

const initVyer = (
  children: React.ReactNode,
  initSidaVidSida: boolean,
  canAdjustSidaVidSida: boolean,
  accordionMode: boolean,
  model?: IClientBusinessModel,
  sideBySideWidths?: SideBySideWidthType[],
  minSideBySideWidths?: SideBySideWidthType[],
  adjustmentButtonsLabel?: string,
  initialActivePages?: number[]
): IObjektVyContextValue => {
  const SOLabel = (model && model.RootType.SOLabel) || '-';
  const collectionName = (model && model.CollectionName) || '-';
  // If we use canAdjustSidaVidSida then we can assume that we have values in localstorage otherwise we just use sideBySideWidths
  const widthsFromLocalStorage =
    canAdjustSidaVidSida &&
    localStorage.getItem(`aveny:${collectionName}:sideBySideWidth`)
      ? JSON.parse(
          localStorage.getItem(`aveny:${collectionName}:sideBySideWidth`) ??
            JSON.stringify(sideBySideWidths ?? [])
        )
      : sideBySideWidths;

  const pages = times<ISidTyp>(
    idx => ({
      caption: idx === 0 ? SOLabel : '-',
      collectionName: idx === 0 ? collectionName : '-',
      aktiv: initSidaVidSida
        ? idx <= 1
        : initialActivePages
        ? initialActivePages.includes(idx)
        : idx === 0,
      size: widthsFromLocalStorage?.[idx],
      minSize: minSideBySideWidths?.[idx],
      hidden: false,
    }),
    pageCount(children)
  );
  return {
    adjustmentButtonsLabel: adjustmentButtonsLabel ?? 'Fördelning mellan sidor',
    businessModel: model,
    canAdjustSidaVidSida: canAdjustSidaVidSida,
    sidaVidSida: initSidaVidSida,
    sidor: pages,
    activePages: pages.filter(page => page.aktiv),
    accordionMode,
  };
};

const SidaVidSidaKolumn: React.FC<
  React.PropsWithChildren<{
    size?: SideBySideWidthType;
  }>
> = props => (
  <EDGrid
    style={{ height: '100%', paddingTop: 0 }}
    item
    xs={6}
    md={props.size ?? 6}
    paddingTop={0}
  >
    {props.children}
  </EDGrid>
);
const SidaVidSidaKolumner: React.FC<React.PropsWithChildren<{}>> = props => (
  <EDMasonry fullPage hideOverflow>
    <EDGrid
      style={{ height: '100%' }}
      container
      paddingTop={0}
      marginTop={0}
      paddingBottom={0}
      marginBottom={0}
    >
      {props.children}
    </EDGrid>
  </EDMasonry>
);

export const ObjektVyBase = (props: IProps) => {
  const formContext = useContext(BusinessDataFormContext);
  const visningTyp = useContext(VisningsTypContext);
  const { menuReference } = useContext(BreadcrumbsContext);
  const { activeRoutine } = useContext(RutinSettingsContext);
  const isFloatingMenuVisible = useIsFloatingMenuVisible();
  const directEditMode =
    rutiner[activeRoutine?.routine.rutinKod || '']?.directEditMode;

  const isLarge = useIsLargeScreen();
  const isExtraSmall = useIsExtraSmallScreen();
  const accordionMode = props.accordionMode ?? 'never';
  const useAccordion =
    accordionMode === 'always' ||
    (accordionMode === 'onMobile' && isExtraSmall);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const startedLarge = useMemo(() => isLarge, []);

  const vyContext = useReducer(vyReducer, undefined, () =>
    initVyer(
      props.children,
      Boolean(props.sidaVidSida) && startedLarge,
      Boolean(props.canAdjustSidaVidSida) &&
        Boolean(props.sidaVidSida) &&
        startedLarge,
      useAccordion,
      formContext ? formContext.model : undefined,
      props.sideBySideWidths,
      props.minSideBySideWidths,
      props.adjustmentButtonsLabel,
      props.initialActivePages
    )
  );
  const [vyContextValue, dispatchVyContext] = vyContext;

  const { currentNavLevel, topNavLevel } = useContext(BreadcrumbsContext);

  // Updatera Sida vid sida så att den stämmer med showDetailsRight
  useEffect(() => {
    const sidaVidSida =
      Boolean(props.sidaVidSida) &&
      startedLarge &&
      !visningTyp.showDetailsRight;

    if (sidaVidSida) {
      dispatchVyContext({
        type: 'setSidaVidSida',
        sidaVidSida,
      });
    }
  }, [
    dispatchVyContext,
    props.sidaVidSida,
    startedLarge,
    visningTyp.showDetailsRight,
  ]);

  const setAktuellaVyer = (vyer: number[]) => {
    dispatchVyContext({ type: 'setAktuellaVyer', aktuellaVyer: vyer });
  };
  const sidor = vyContextValue.sidor;

  const antalAktivaSidor = sidor.filter(s => s.aktiv).length;

  const children = Array.isArray(props.children)
    ? props.children.map((child, idx) =>
        sidor[idx]?.aktiv && !useAccordion ? (
          antalAktivaSidor > 1 ? (
            <SidaVidSidaKolumn key={idx} size={sidor[idx]?.size}>
              {child}
            </SidaVidSidaKolumn>
          ) : (
            child
          )
        ) : (
          child
        )
      )
    : props.children;
  const sidaVidSidaAktiv = sidor.filter(sida => sida.aktiv).length > 1;

  const displayVyValjare =
    !useAccordion &&
    (!formContext?.formState.editMode ||
      formContext?.fullScreen ||
      directEditMode ||
      visningTyp.showDetailsRight);

  if (formContext == null) {
    return (
      <InternalError
        id="40c851e8-1a6e-452d-95d0-c2817f75bfcd"
        message="ObjektVyBase utanför formcontext"
      />
    );
  }

  const editMode = formContext.formState.editMode;
  const editMenuVisible =
    !props.hideEditMenu && (editMode || Boolean(isFloatingMenuVisible));
  const stepperBarVisible =
    isExtraSmall &&
    !useAccordion &&
    !(editMenuVisible && isFloatingMenuVisible) &&
    (!formContext.formState.editMode || directEditMode) &&
    currentNavLevel === topNavLevel;

  const haveVisibleViews = sidor.filter(vy => !vy.hidden).length !== 0;
  const funktionsMenyItems: IFunctionMenuItem[] = directEditMode
    ? props.funktionsMenyItems ?? []
    : [
        {
          id: 'base',
          caption: formContext.model
            ? `Redigera ${formContext.model.RootType.SOLabel}`
            : 'Redigera',
          iconType: 'edit',
          onClick: () =>
            formContext.formStateDispatch({
              type: 'ENTER_EDITMODE',
            }),
          disabled: !props.editImplemented && props.editButtonDisabled,
          disabledReason:
            props.editButtonDisabledReason ?? 'Formulär ej redigerbart ännu',
        },
        ...(props.funktionsMenyItems ?? []),
      ];

  return (
    <ObjektVyContext.Provider value={vyContext}>
      <EDView
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          flex: 'inherit',
        }}
      >
        {displayVyValjare && haveVisibleViews && (
          <VyValjare
            setAktuellaVyer={setAktuellaVyer}
            vyer={vyContextValue.sidor}
            tabbed
            sidaVidSida={
              props.sidaVidSida && startedLarge && !visningTyp.showDetailsRight
            }
            fullScreen={formContext.fullScreen}
          />
        )}
        <EDBox
          sx={{
            display: 'flex',
            flex: '1 1 auto',
            flexDirection: 'column',
            overflowY: sidaVidSidaAktiv ? 'hidden' : 'auto',
            overflowX: sidaVidSidaAktiv ? 'hidden' : 'visible',
          }}
        >
          {sidaVidSidaAktiv ? (
            <SidaVidSidaKolumner>{children}</SidaVidSidaKolumner>
          ) : (
            children
          )}
        </EDBox>
        {editMenuVisible && (
          <BusinessDataFormEditMenu
            directEditMode={directEditMode}
            onCancel={props.onCancel}
            rutinKod={activeRoutine?.routine.rutinKod}
            functionMenuItems={
              isFloatingMenuVisible &&
              !(directEditMode
                ? formContext.formState.formHasChanged
                : formContext.formState.editMode)
                ? funktionsMenyItems
                : undefined
            }
          />
        )}
        {stepperBarVisible && <StepperBar editMenuVisible={editMenuVisible} />}
        {menuReference &&
          !isFloatingMenuVisible &&
          (!directEditMode || funktionsMenyItems.length > 0) &&
          (currentNavLevel || 0) + (visningTyp.showDetailsRight ? 1 : 0) ===
            topNavLevel &&
          createPortal(
            <FunctionMenu
              id=""
              highEmphasis
              disabled={
                directEditMode
                  ? formContext.formState.formHasChanged
                  : formContext.formState.editMode
              }
              testId="FunctionMenu-NavBar"
            >
              {funktionsMenyItems.map(funktionsMenyItem => (
                <FunctionItem
                  key={funktionsMenyItem.id}
                  iconType={funktionsMenyItem.iconType}
                  onClick={funktionsMenyItem.onClick}
                  disabled={funktionsMenyItem.disabled}
                  disabledReason={funktionsMenyItem.disabledReason}
                  listItemIcon={directEditMode ? true : undefined}
                  confirmButtonCaption={funktionsMenyItem.confirmButtonCaption}
                  confirmDialogCaption={funktionsMenyItem.confirmDialogCaption}
                  confirmDialogMessage={funktionsMenyItem.confirmDialogMessage}
                >
                  {funktionsMenyItem.caption}
                </FunctionItem>
              ))}
            </FunctionMenu>,
            menuReference
          )}
      </EDView>
    </ObjektVyContext.Provider>
  );
};

const ObjektVy: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  return (
    <ErrorBoundary name="ObjektVy">
      {props.error ? (
        <InternalError
          id="7127a667-f1d4-4bfa-b40f-d96710c8c395"
          message={props.error.message}
          error={props.error}
          skipErrorLogging
        />
      ) : (
        <ObjektVyBase {...props} />
      )}
    </ErrorBoundary>
  );
};

export default ObjektVy;
